import React from 'react';
import { HelpCircle, Heart, TrendingUp } from 'lucide-react';
import { TaraCard, TaraCardHeader, TaraCardTitle, TaraCardDescription, TaraCardContent } from '../components/ui/TaraCard';
import TaraBadge from '../components/ui/TaraBadge';
import BottomNav from '../components/BottomNav';

const DashboardNew = () => {
  return (
    <div className="min-h-screen bg-stone-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-5 py-4 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <h1 className="font-serif text-2xl font-medium text-stone-900">
            Health Dashboard
          </h1>
          <button className="p-2 hover:bg-stone-100 rounded-full transition-colors">
            <HelpCircle className="w-5 h-5 text-stone-600" />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-5 py-6 max-w-5xl mx-auto space-y-6">
        {/* Summary Card */}
        <TaraCard variant="cosmic" className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-serif text-3xl font-medium text-stone-900">
                Vitality: 8.3/10
              </h2>
              <ul className="mt-3 space-y-1 text-sm text-stone-600">
                <li>• High resilience period till 2027</li>
                <li>• Focus on sleep + back posture</li>
              </ul>
            </div>
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
              <Heart className="w-10 h-10 text-green-600" />
            </div>
          </div>
        </TaraCard>
        
        {/* Grid Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Health Timeline */}
          <TaraCard interactive>
            <TaraCardHeader>
              <TaraCardTitle>Health Timeline</TaraCardTitle>
              <TaraCardDescription>
                Next 12 months key events
              </TaraCardDescription>
            </TaraCardHeader>
            <TaraCardContent>
              <div className="space-y-3">
                {[
                  { month: 'Mar 2025', event: 'Energy peak phase', type: 'positive' },
                  { month: 'Jun 2025', event: 'Monitor digestion', type: 'watchful' },
                  { month: 'Sep 2025', event: 'Joint care focus', type: 'watchful' },
                ].map((item) => (
                  <div key={item.month} className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      item.type === 'positive' ? 'bg-green-500' : 'bg-amber-500'
                    }`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-stone-900">{item.month}</p>
                      <p className="text-xs text-stone-600">{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </TaraCardContent>
          </TaraCard>
          
          {/* Disease Radar */}
          <TaraCard interactive>
            <TaraCardHeader>
              <TaraCardTitle>Disease Risk Radar</TaraCardTitle>
              <TaraCardDescription>
                Astrological risk assessment
              </TaraCardDescription>
            </TaraCardHeader>
            <TaraCardContent>
              <div className="space-y-2">
                {[
                  { disease: 'Diabetes', risk: 'Low', variant: 'calm' },
                  { disease: 'Heart', risk: 'Low', variant: 'calm' },
                  { disease: 'Arthritis', risk: 'Moderate', variant: 'watchful' },
                ].map((item) => (
                  <div key={item.disease} className="flex items-center justify-between">
                    <span className="text-sm text-stone-700">{item.disease}</span>
                    <TaraBadge variant={item.variant}>
                      {item.risk}
                    </TaraBadge>
                  </div>
                ))}
              </div>
            </TaraCardContent>
          </TaraCard>
          
          {/* Organ Focus */}
          <TaraCard interactive>
            <TaraCardHeader>
              <TaraCardTitle>Organ Focus</TaraCardTitle>
              <TaraCardDescription>
                Areas needing attention
              </TaraCardDescription>
            </TaraCardHeader>
            <TaraCardContent>
              <div className="flex flex-wrap gap-2">
                {['Heart', 'Lungs', 'Joints', 'Digestive'].map((organ) => (
                  <TaraBadge key={organ} size="lg">
                    {organ}
                  </TaraBadge>
                ))}
              </div>
            </TaraCardContent>
          </TaraCard>
          
          {/* Remedies */}
          <TaraCard interactive>
            <TaraCardHeader>
              <TaraCardTitle>Remedies & Habits</TaraCardTitle>
              <TaraCardDescription>
                Personalized suggestions
              </TaraCardDescription>
            </TaraCardHeader>
            <TaraCardContent>
              <div className="space-y-2">
                {[
                  '🧘 Pranayama 10 min daily',
                  '☀️ Sunrise vitamin D exposure',
                  '🥗 Turmeric in meals',
                ].map((remedy) => (
                  <div key={remedy} className="text-sm text-stone-700">
                    {remedy}
                  </div>
                ))}
              </div>
            </TaraCardContent>
          </TaraCard>
        </div>
        
        {/* Full-width Transit Card */}
        <TaraCard variant="cosmic">
          <TaraCardHeader>
            <TaraCardTitle>Upcoming Transit</TaraCardTitle>
            <TaraCardDescription>
              Saturn enters 7th house in March 2026
            </TaraCardDescription>
          </TaraCardHeader>
          <TaraCardContent>
            <p className="text-sm text-stone-700">
              This marks a 2.5-year period of introspection and relationship focus. Health-wise, pay attention to bones and joints.
            </p>
          </TaraCardContent>
        </TaraCard>
      </div>
      
      <BottomNav active="timeline" />
    </div>
  );
};

export default DashboardNew;